/**
 * The several-models forecast for one spot on the chart.
 *
 * Which models to compare is the skipper's choice and it persists, because it is
 * a preference about how they like to read a forecast rather than a thing to
 * re-pick every time they tap the water.
 *
 * The fetch is keyed by the rounded position AND the set of models, so tapping
 * about the same bay re-reads the cache while genuinely moving, or adding a
 * model, goes and gets more. The whole hourly series for every model is held, so
 * scrolling the hours and flicking between days costs nothing once it has
 * landed.
 */
import { create } from 'zustand';
import {
  fetchForecastTable,
  WEATHER_VARIABLES,
  type ForecastTable,
} from '@/weather/multiModel';
import { WEATHER_MODELS } from '@/weather/models';
import { loadJson, saveJson } from '@/store/persist';

const MODELS_LS = 'acp.forecastModels.v1';

/**
 * Three to open with: the UK model because it is home water, ECMWF because it is
 * the best-regarded global one and runs furthest, and ICON as an independent
 * European opinion. Enough to see disagreement without six columns on a phone.
 */
const DEFAULT_MODEL_IDS = ['ukmo_seamless', 'ecmwf_ifs', 'icon_seamless'];

function loadModelIds(): string[] {
  const saved = loadJson<string[]>(MODELS_LS, DEFAULT_MODEL_IDS);
  const known = Array.isArray(saved)
    ? saved.filter((id) => WEATHER_MODELS.some((m) => m.id === id))
    : [];
  // An empty pick would show a table with nothing in it and no way back, so a
  // saved list that no build recognises falls back rather than sticking.
  return known.length ? known : DEFAULT_MODEL_IDS;
}

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface ForecastTableState {
  /** Which models are being compared, in catalogue order. */
  modelIds: string[];
  setModelIds: (ids: string[]) => void;
  toggleModel: (id: string) => void;

  table: ForecastTable | null;
  status: Status;
  error: string | null;
  key: string | null;

  load: (position: [number, number]) => Promise<void>;
  clear: () => void;
}

/** Two decimals is about a mile: the same forecast either side of an anchorage. */
function keyFor(position: [number, number], modelIds: string[]): string {
  return `${position[0].toFixed(2)},${position[1].toFixed(2)}:${[...modelIds].sort().join('+')}`;
}

export const useForecastTableStore = create<ForecastTableState>((set, get) => ({
  modelIds: loadModelIds(),
  setModelIds: (ids) => {
    saveJson(MODELS_LS, ids);
    // The cached table was fetched for the old set, so it is no longer the
    // answer to the question being asked.
    set({ modelIds: ids, key: null, table: null, status: 'idle' });
  },
  toggleModel: (id) => {
    const cur = get().modelIds;
    const next = cur.includes(id) ? cur.filter((m) => m !== id) : [...cur, id];
    // Never down to nothing: a table with no columns is a dead end.
    if (!next.length) return;
    get().setModelIds(WEATHER_MODELS.filter((m) => next.includes(m.id)).map((m) => m.id));
  },

  table: null,
  status: 'idle',
  error: null,
  key: null,

  load: async (position) => {
    const modelIds = get().modelIds;
    const key = keyFor(position, modelIds);
    const cur = get();
    if (cur.key === key && (cur.table || cur.status === 'loading')) return;

    set({ key, status: 'loading', error: null });
    try {
      const models = WEATHER_MODELS.filter((m) => modelIds.includes(m.id));
      const table = await fetchForecastTable(position, models, WEATHER_VARIABLES);
      if (get().key !== key) return; // the finger moved on
      set({ table, status: 'ready', error: null });
    } catch (e) {
      if (get().key !== key) return;
      set({ status: 'error', error: e instanceof Error ? e.message : 'Forecast fetch failed' });
    }
  },

  clear: () => set({ table: null, status: 'idle', error: null, key: null }),
}));
